/**
 * Product naming for user-facing copy.
 *
 * The `utas/` build is white-labelled: it ships to uas.draydog.com and is
 * embedded as an iframe inside Wabtec's Port Optimizer portal, where users have
 * never heard of Dray Dog. It has its own App.vue, router and header, but it
 * renders shared components straight out of `src/` — `utas/src/pages/appointments.vue`
 * mounts `~/components/dispatch_table/DispatchTableAndControls.vue`, so most of
 * the dispatch table and every dialog hanging off it is shared code.
 *
 * That means **shared `src/` components must never hardcode the brand name** —
 * use `BRAND_NAME` instead. `test/brandNameLiterals.test.ts` enforces this.
 */

// Deliberately not exported. A shared component importing one of these
// directly would defeat the whole convention, and `brandNameLiterals.test.ts`
// could not catch it — its regex needs a space between the two words, which
// `BRAND_NAME_DRAY_DOG` doesn't have. `BRAND_NAME` is the only way in.
const BRAND_NAME_DRAY_DOG = 'Dray Dog'
const BRAND_NAME_PORT_OPTIMIZER = 'Port Optimizer'

/**
 * Split out from the `BRAND_NAME` constant below purely so both branches are
 * unit-testable: `__IS_UTAS_APP__` is a Vite `define`, so it is textually
 * inlined at build time and `vi.stubGlobal` cannot flip it.
 *
 * Rolldown doesn't fold the define through this call, so both names ship in
 * both bundles (~15 wasted bytes) and the pick happens on first evaluation.
 * Worth it for the test coverage.
 */
export function resolveBrandName(isUtasApp: boolean): string {
  return isUtasApp ? BRAND_NAME_PORT_OPTIMIZER : BRAND_NAME_DRAY_DOG
}

/** The product name to use in user-facing copy. */
export const BRAND_NAME = resolveBrandName(__IS_UTAS_APP__)
